import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LazyLoadingLibraryService } from '~/modules/stocks/reports/report/LazyLoadingLibraryService';
import { ReportsService } from '~/services/modules/reports/reports';
import { ThemeManagerService } from '~/services/theme-manager';
import { IQrBarcodeModel, IQrReportObject } from './models/qr-label.model';
import { QrPayloadEncoderUtil } from './utils/qr-payload-encoder.util';
import { QrReportLayoutBuilder } from './utils/qr-report-layout-builder';

declare var Stimulsoft: any;

const STIMULSOFT_LICENSE_KEY =
  '6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHkUqG1JqVi9xF1CsQtSylmNCw08f5US/nThDcH2mIyIhcb9q2' +
  'eL238CYd2WruGTJRQWTjv4Ojri9sZfpmunjsBAwTcJRB+7bLz0yAVCU6zESyCSrqmTsZGqSSjXhq82ybmFzAFUskAo' +
  '4HStx+kugphgl4gcDP0VI2kgHB6uQSOD2Xh8X+fmtkH1Q6RMplr+plgH0lQ35cSNk7kDJvc07nTPGVv+ixfOJYHyWO' +
  'o+A6M93S04A7HjpQFnJHCQqzJ7lJ3ZO9heRh2Vt5VBfUJoQZIFTpTNCAvXKZ6FmbFtIa8lKG0CGWFBJjDFkh6e6yVf' +
  'IRPFT48DDBGyC1ZZXHFTiCSxTxx9Rn+ZQrLDgvhZa4YFVIBm0Rrekr67eVHUqXpL7IiQA6kbCkJ/LZ6LCJwEL0TbBn' +
  'RWDfcx/r65mXVC/HoS+KZ3MWfB1fqZmiLfeWjTXXvnE3LjxgJYGobOqvdZns8d9c/Xq+cZtW6/wjnZL6SrHSjaUJPc' +
  'rLgViTggF/EzQGigT6qbzY1Cf5YNhWCuYaN+ou0vG9LVmMSR6oPxc5eHphDFlzEVgUHZWeyGlX0AVp7HVFVWUiJevw' +
  '9NbBkdYnR+0Iqs9pwBbvH3fgbFM8C9eyWM41wmJl16/jqohlJZHuw=';

@Component({
    selector: 'qr-report-viewer',
    templateUrl: './qr-report-viewer.component.html',
    styleUrls: ['./qr-report-viewer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QrReportViewerComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() reportObject: IQrReportObject;
  @Input() barcodeModel: IQrBarcodeModel;
  @Input() previewMode: boolean = false;
  @Input() windowMode: boolean = true;
  @Input() isAsset: boolean = false;

  @Output() closeForm = new EventEmitter<void>();
  @Output() returnDefinition = new EventEmitter<any>();
  @Output() maximizeWindow = new EventEmitter<void>();

  @ViewChild('qrViewerHost', { static: false }) qrViewerHost: ElementRef;

  reportFileLoaded = false;
  viewerFileLoaded = false;
  designerFileLoaded = false;

  stimulsoftReport: any;
  viewer: any;
  options: any;
  dataSet: any;
  uniqueContainerId: string = 'qr_sti_viewer_' + Math.random().toString(36).substring(2, 9);
  theme: string = 'light';

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private _lazyLoadingLibraryService: LazyLoadingLibraryService,
    private _reportService: ReportsService,
    private _themeManager: ThemeManagerService
  ) {
    if (this._themeManager?.themeChange) {
      this._themeManager.themeChange.pipe(takeUntil(this._destroy$)).subscribe((t: string) => {
        this.theme = t || 'light';
      });
    }
  }

  ngOnInit(): void {
    if (this.barcodeModel && (!this.barcodeModel.numberOfCopy || this.barcodeModel.numberOfCopy < 1)) {
      this.barcodeModel.numberOfCopy = 1;
    }
  }

  ngAfterViewInit(): void {
    if (typeof Stimulsoft !== 'undefined' && Stimulsoft.Report && Stimulsoft.Viewer) {
      this.reportFileLoaded = true;
      this.viewerFileLoaded = true;
      this._initReport();
    } else {
      this._loadStimulsoftScripts();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.reportObject || changes.barcodeModel) {
      if (typeof Stimulsoft !== 'undefined' && Stimulsoft.Report && Stimulsoft.Viewer) {
        this.reportFileLoaded = true;
        this.viewerFileLoaded = true;
        this._initReport();
      }
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();

    if (this.viewer) {
      try {
        this.viewer.report = null;
      } catch (e) {}
    }
    if (this.stimulsoftReport) {
      try {
        this.stimulsoftReport.dispose();
      } catch (e) {}
    }
  }

  private _loadStimulsoftScripts(): void {
    this._lazyLoadingLibraryService
      .loadJs('assets/scripts/stimulsoft.reports.pack.js')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.reportFileLoaded = true;
        if (this.viewerFileLoaded) this._initReport();
      });

    this._lazyLoadingLibraryService
      .loadJs('assets/scripts/stimulsoft.viewer.pack.js')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.viewerFileLoaded = true;
        if (this.reportFileLoaded) this._initReport();
      });
  }

  private _initReport(): void {
    if (typeof Stimulsoft === 'undefined' || !Stimulsoft.Report || !Stimulsoft.Viewer) {
      return;
    }
    this._initStimulsoft();
    this._generateQrReport();
  }

  private _initStimulsoft(): void {
    Stimulsoft.Base.StiLicense.key = STIMULSOFT_LICENSE_KEY;

    this.stimulsoftReport = new Stimulsoft.Report.StiReport();
    this.options = new Stimulsoft.Viewer.StiViewerOptions();
    this.options.appearance.defaultUnit = Stimulsoft.Report.StiReportUnitType.Inches;
    this.options.toolbar.showDesignButton = false;
    this.options.toolbar.showAboutButton = false;
    this.options.toolbar.showOpenButton = false;

    this.viewer = new Stimulsoft.Viewer.StiViewer(this.options, this.uniqueContainerId, false);
  }

  private _generateQrReport(): void {
    const model = this.barcodeModel || this.reportObject?.barcodeModel;
    if (!model) return;

    let barcodeValues: any[] = model.BarcodeValues || [];
    if ((!barcodeValues || barcodeValues.length === 0) && this.reportObject?.ReportObj) {
      if (typeof this.reportObject.ReportObj.addLogo === 'string') {
        this.reportObject.ReportObj.addLogo = this.reportObject.ReportObj.addLogo === 'Yes';
      }
      if (typeof this.reportObject.ReportObj.addAddress === 'string') {
        this.reportObject.ReportObj.addAddress = this.reportObject.ReportObj.addAddress === 'Yes';
      }

      this._reportService.PreviewReportData(this.reportObject.ReportObj)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (res: any) => {
            const rows = (res && Array.isArray(res)) ? res.slice(0, 20) : [];
            const mappedValues = this._mapPreviewResponseToBarcodeValues(rows);
            this._renderQrReportWithValues(mappedValues, model);
          },
          error: (err: any) => {
            console.error('PreviewReportData failed, using fallback preview data:', err);
            const mappedValues = this._mapPreviewResponseToBarcodeValues([]);
            this._renderQrReportWithValues(mappedValues, model);
          }
        });
      return;
    }

    const mappedValues = this._mapPreviewResponseToBarcodeValues(barcodeValues);
    this._renderQrReportWithValues(mappedValues, model);
  }

  private _getReportCategoryId(): any {
    return (
      this.reportObject?.ReportObj?.reportCategoryId ??
      this.reportObject?.ReportObj?.reportCategoryid ??
      this.reportObject?.ReportObj?.LabelPreset?.reportCategoryId ??
      this.reportObject?.ReportObj?.labelPreset?.reportCategoryId ??
      this.reportObject?.reportCategoryId ??
      this.barcodeModel?.reportCategoryId ??
      this.barcodeModel?.reportCategoryid ??
      this.barcodeModel?.LabelPresetId?.reportCategoryId ??
      this.barcodeModel?.labelPreset?.reportCategoryId ??
      this.reportObject?.ReportObj?.LabelPreset?.pageName ??
      this.reportObject?.ReportObj?.labelPreset?.pageName ??
      this.reportObject?.ReportObj?.pageName ??
      this.reportObject?.pageName ??
      this.barcodeModel?.LabelPresetId?.pageName ??
      this.barcodeModel?.labelPreset?.pageName ??
      this.barcodeModel?.pageName ??
      null
    );
  }

  private _getLabelId(): string | null {
    const raw = (
      this.reportObject?.ReportObj?.LabelPreset?.id ??
      this.reportObject?.ReportObj?.labelPreset?.id ??
      this.reportObject?.ReportObj?.labelPresetId ??
      this.reportObject?.ReportObj?.LabelPresetId ??
      this.reportObject?.ReportObj?.id ??
      (typeof this.barcodeModel?.LabelPresetId === 'object' ? (this.barcodeModel.LabelPresetId.id ?? this.barcodeModel.LabelPresetId.Id) : this.barcodeModel?.LabelPresetId) ??
      this.barcodeModel?.labelPreset?.id ??
      this.barcodeModel?.id ??
      null
    );
    return raw ? String(raw) : null;
  }

  private _getReportName(): string {
    return (
      this.reportObject?.ReportObj?.name ||
      this.reportObject?.ReportObj?.LabelPreset?.name ||
      this.reportObject?.ReportObj?.labelPreset?.name ||
      this.reportObject?.name ||
      this.barcodeModel?.name ||
      this.barcodeModel?.labelPreset?.name ||
      ''
    );
  }

  private _getFilterByField(): string {
    const raw = (
      this.reportObject?.ReportObj?.selectByField ||
      this.reportObject?.ReportObj?.filterBy ||
      this.reportObject?.selectByField ||
      this.reportObject?.filterBy ||
      this.barcodeModel?.selectByField ||
      this.barcodeModel?.filterBy ||
      this.barcodeModel?.LabelPresetId?.selectByField ||
      this.barcodeModel?.LabelPresetId?.filterBy ||
      this.barcodeModel?.labelPreset?.selectByField ||
      this.barcodeModel?.labelPreset?.filterBy ||
      this.reportObject?.ReportObj?.LabelPreset?.selectByField ||
      this.reportObject?.ReportObj?.LabelPreset?.filterBy ||
      ''
    );
    let clean = (typeof raw === 'object' ? (raw.field || raw.title || '') : String(raw))
      .replace('(barcode image)', '')
      .replace('(QRCode image)', '')
      .trim();

    if (clean.includes('.')) {
      clean = clean.substring(clean.lastIndexOf('.') + 1).trim();
    }
    return clean;
  }

  private _mapPreviewResponseToBarcodeValues(rows: any[]): any[] {
    debugger
    const selectByField = this._getFilterByField();
    const allFieldObjs = QrPayloadEncoderUtil.extractFieldMappings(this.reportObject, selectByField);

    const firstField = allFieldObjs[0];
    const qrFieldKey = selectByField || firstField?.field || '';

    if (!rows || rows.length === 0) {
      const sampleItem: any = {};
      allFieldObjs.forEach((f) => {
        sampleItem[f.field] = `Sample ${f.title || f.field}`;
      });
      if (qrFieldKey) {
        sampleItem[qrFieldKey] = 'AST-1001';
      }
      sampleItem.BarcodeNumber = 'AST-1001';
      rows = [sampleItem];
    }

    const isUrl = (
      this.barcodeModel?.isURL ??
      this.barcodeModel?.isUrl ??
      this.barcodeModel?.LabelPresetId?.isURL ??
      this.barcodeModel?.LabelPresetId?.isUrl ??
      this.barcodeModel?.labelPreset?.isURL ??
      this.barcodeModel?.labelPreset?.isUrl ??
      this.reportObject?.ReportObj?.LabelPreset?.isURL ??
      this.reportObject?.ReportObj?.LabelPreset?.isUrl ??
      this.reportObject?.ReportObj?.labelPreset?.isURL ??
      this.reportObject?.ReportObj?.labelPreset?.isUrl ??
      this.reportObject?.ReportObj?.isURL ??
      this.reportObject?.ReportObj?.isUrl ??
      this.reportObject?.isURL ??
      this.reportObject?.isUrl ??
      true
    );

    return rows.map((item: any) => {
      let qrValue: string;
      if (isUrl) {
        const parts = allFieldObjs.map((f) => {
          const val = QrPayloadEncoderUtil.resolveFieldValue(item, f.field, f.title, 'AST-1001');
          return `${f.title}: ${val}`;
        }).join('<br/><br/>');

        const catId = this._getReportCategoryId();
        const labelId = this._getLabelId();
        const reportName = this._getReportName();
        const filterByField = this._getFilterByField() || qrFieldKey;
        const filterVal = filterByField ? QrPayloadEncoderUtil.resolveFieldValue(item, filterByField, '', '') : '';

        qrValue = QrPayloadEncoderUtil.buildQrTargetUrl({
          labelId: labelId || undefined,
          catId: catId || undefined,
          name: reportName || undefined,
          filterBy: filterByField || undefined,
          val: filterVal || undefined,
          data: parts
        });
      } else {
        qrValue = QrPayloadEncoderUtil.resolveFieldValue(
          item,
          qrFieldKey,
          firstField?.title,
          item.BarcodeNumber || item.barcodeNumber || item.code || item.id || 'QR-CODE'
        );
      }

      const textParts = allFieldObjs
        .filter(f => !f.isQrOnly)
        .map((f) => QrPayloadEncoderUtil.resolveFieldValue(item, f.field, f.title, ''))
        .filter((v: string) => v !== '');
      const displayName = textParts.length > 0
        ? textParts.join(' | ')
        : QrPayloadEncoderUtil.resolveFieldValue(item, qrFieldKey, '', String(qrValue));

      return {
        ...item,
        BarcodeNumber: String(qrValue),
        displayName: displayName
      };
    });
  }

  private _renderQrReportWithValues(barcodeValues: any[], model: IQrBarcodeModel): void {
    const copies = model.numberOfCopy || 1;
    if (copies > 1) {
      barcodeValues = this._multiplyCopies(barcodeValues, copies);
    }

    const labelSize = model.LabelSize || {};
    const labelSizeID = model.LabelSizeID || labelSize.id || 0;
    const isAvery = labelSizeID > 0 && labelSizeID < 11;

    this.stimulsoftReport.reportUnit = Stimulsoft.Report.StiReportUnitType.Inches;
    this.stimulsoftReport.pages.clear();

    const page = new Stimulsoft.Report.Components.StiPage(this.stimulsoftReport);
    page.name = 'QrPage1';
    page.margins = new Stimulsoft.Report.Components.StiMargins(
      labelSize.leftMargin || 0.15,
      labelSize.rightMargin || 0.15,
      labelSize.topMargin || 0.5,
      labelSize.bottomMargin || 0.5
    );
    this.stimulsoftReport.pages.add(page);

    if (isAvery) {
      QrReportLayoutBuilder.buildAveryLayout(page, barcodeValues, labelSize, model);
    } else {
      QrReportLayoutBuilder.buildZebraLayout(page, barcodeValues, labelSize);
    }

    this.stimulsoftReport.renderAsync(() => {
      this.viewer.report = this.stimulsoftReport;
      setTimeout(() => {
        this.viewer.renderHtml(this.uniqueContainerId);
      }, 50);
      try {
        const def = this.stimulsoftReport.saveToJsonString();
        this.returnDefinition.emit(def);
      } catch (e) {}
    });
  }

  private _multiplyCopies(data: any[], copies: number): any[] {
    const result: any[] = [];
    for (const item of data) {
      for (let c = 0; c < copies; c++) {
        result.push({ ...item });
      }
    }
    return result;
  }
}
